import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../../lib/apiClient";
import { ig, igGlobalStyles, Badge } from "../inbox/_inbox_shared";
import {
  ArrowLeft, Pencil, Undo2, Redo2, BarChart3, ChevronDown,
  Plus, Copy, Trash2, Hand, Minus, Maximize2, Sparkles, AlertCircle,
} from "lucide-react";
import TriggerPicker from "./TriggerPicker";
import ActionPicker from "./ActionPicker";
import NodeConfigDrawer from "./NodeConfigDrawer";
import { NODE_STYLE, TONES } from "./_automation_data";

const TABS = [
  { k: "builder",   label: "Builder" },
  { k: "settings",  label: "Settings" },
  { k: "history",   label: "Enrollment history" },
  { k: "logs",      label: "Execution logs" },
];

export default function WorkflowBuilder() {
  const { workflowId } = useParams();
  const nav = useNavigate();
  const [wf, setWf] = useState(null);
  const [tab, setTab] = useState("builder");
  const [pickerOpen, setPickerOpen] = useState(null); // null | { mode: 'trigger'|'action', insertAfter?: nodeId }
  const [editNode, setEditNode] = useState(null);
  const [enrollments, setEnrollments] = useState(null);
  const [logs, setLogs] = useState(null);
  const [saveStatus, setSaveStatus] = useState("Saved");

  const reload = useCallback(() => {
    api.get(`/api/automation/workflows/${workflowId}`).then(setWf);
  }, [workflowId]);
  useEffect(() => { reload(); }, [reload]);
  useEffect(() => {
    if (tab === "history") api.get(`/api/automation/workflows/${workflowId}/enrollments`).then((d) => setEnrollments(d.items));
    if (tab === "logs")    api.get(`/api/automation/workflows/${workflowId}/logs`).then((d) => setLogs(d.items));
  }, [tab, workflowId]);

  const save = async (partial) => {
    setSaveStatus("Saving…");
    try {
      const r = await api.patch(`/api/automation/workflows/${workflowId}`, partial);
      setWf(r);
      setSaveStatus("Saved");
    } catch (e) { setSaveStatus("Unsaved"); }
  };

  const addTrigger = (key, label) => {
    const newId = `tr-${Date.now()}`;
    const newNode = { id: newId, kind: "trigger", type: key, label,
      summary: `Trigger: ${label}`, config: {}, x: 0, y: 0 };
    const nodes = [...(wf.nodes || []), newNode];
    save({ nodes });
    setPickerOpen(null);
  };

  const addAction = (key, label, afterNodeId) => {
    const newId = `a-${Date.now()}`;
    const newNode = { id: newId, kind: "action", type: key, label,
      summary: `${label}`, config: {}, x: 0, y: 0 };
    const nodes = [...(wf.nodes || []), newNode];
    // Insert edge from "afterNodeId" → newNode. If afterNodeId not provided, link from the last node in the main chain.
    const edges = [...(wf.edges || [])];
    const source = afterNodeId
      || ([...nodes].reverse().find((n) => n.id !== newId && (n.kind === "action" || nodes.filter(x => x.kind === "trigger").length === 1))?.id)
      || nodes.find((n) => n.kind === "trigger")?.id;
    if (source) edges.push({ id: `e-${source}-${newId}`, source, target: newId });
    save({ nodes, edges });
    setPickerOpen(null);
  };

  const updateNode = (nodeId, updates) => {
    const nodes = (wf.nodes || []).map((n) => n.id === nodeId ? { ...n, ...updates } : n);
    save({ nodes });
  };
  const removeNode = (nodeId) => {
    const nodes = (wf.nodes || []).filter((n) => n.id !== nodeId);
    const edges = (wf.edges || []).filter((e) => e.source !== nodeId && e.target !== nodeId);
    save({ nodes, edges });
  };
  const duplicateNode = (nodeId) => {
    const node = (wf.nodes || []).find((n) => n.id === nodeId);
    if (!node) return;
    const newId = `${node.kind === "trigger" ? "tr" : "a"}-${Date.now()}`;
    const newNode = { ...node, id: newId };
    save({ nodes: [...(wf.nodes || []), newNode] });
  };

  if (!wf) return <div style={{ padding: 40, color: ig.muted, fontFamily: "Inter, sans-serif" }}>Loading workflow…</div>;

  const triggers = (wf.nodes || []).filter((n) => n.kind === "trigger");
  const actions  = (wf.nodes || []).filter((n) => n.kind === "action");

  return (
    <div data-testid="workflow-builder" style={{
      background: ig.bg, color: ig.ink, minHeight: "calc(100vh - 0px)",
      margin: "-22px -26px -80px", fontFamily: "Inter, system-ui, -apple-system, sans-serif",
    }}>
      <style>{igGlobalStyles}</style>

      {/* TOP BAR */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "12px 20px",
        background: ig.card, borderBottom: `1px solid ${ig.border}` }}>
        <button data-testid="back-to-list" onClick={() => nav("/admin/automation")} style={topGhost}>
          <ArrowLeft size={14} /> Workflows list
        </button>
        <button style={topGhost}><Pencil size={13} /></button>
        <div style={{ flex: 1, textAlign: "center", display: "flex", justifyContent: "center", alignItems: "center", gap: 6 }}>
          <input data-testid="wf-name" defaultValue={wf.name} onBlur={(e) => save({ name: e.target.value })}
            style={{ fontWeight: 800, fontSize: 14, border: 0, outline: "none", background: "transparent",
              color: ig.ink, textAlign: "center", width: 420 }} />
          <Pencil size={12} color={ig.muted} />
        </div>
        <span style={{ fontSize: 11, padding: "5px 10px", borderRadius: 6, fontWeight: 700,
          background: saveStatus === "Saved" ? "#DCFCE7" : "#FEF3C7",
          color: saveStatus === "Saved" ? "#15803D" : "#92400E" }}>{saveStatus}</span>
        <button data-testid="logo" style={{ ...topGhost, color: "#22C55E", border: 0 }}>
          <span style={{ width: 24, height: 24, borderRadius: 6, background: "linear-gradient(135deg, #18B86B, #22C55E)",
            display: "inline-grid", placeItems: "center", color: "#fff", fontWeight: 800 }}>iG</span>
        </button>
        <button data-testid="stats" style={topGhost}><BarChart3 size={13} /></button>
        <button style={topGhost}><Undo2 size={13} /></button>
        <button style={topGhost}><Redo2 size={13} /></button>
        <span style={{ padding: "8px 14px", borderRadius: 8, background: "#DCFCE7", color: "#15803D", fontWeight: 800, fontSize: 12.5 }}>Saved</span>
      </div>

      {/* TABS + Toggle */}
      <div style={{ display: "flex", alignItems: "center", gap: 28, padding: "0 20px",
        borderBottom: `1px solid ${ig.border}`, background: ig.card }}>
        <select data-testid="builder-mode" defaultValue="standard" style={{
          padding: "7px 12px", margin: "10px 0", borderRadius: 8, border: `1px solid ${ig.border}`,
          background: ig.bg, fontSize: 12.5, fontWeight: 700, outline: "none",
        }}>
          <option value="standard">Standard builder</option>
          <option value="advanced">Advanced builder</option>
        </select>
        <div style={{ flex: 1, display: "flex", justifyContent: "center", gap: 26 }}>
          {TABS.map((t) => (
            <button key={t.k} data-testid={`builder-tab-${t.k}`} onClick={() => setTab(t.k)} style={{
              padding: "16px 0", border: 0, background: "transparent", cursor: "pointer",
              color: tab === t.k ? "#2563EB" : ig.muted, fontWeight: 700, fontSize: 13,
              borderBottom: "2px solid " + (tab === t.k ? "#2563EB" : "transparent"),
            }}>{t.label}</button>
          ))}
        </div>
        <button data-testid="test-workflow" style={{
          padding: "7px 14px", borderRadius: 8, border: `1px solid #BFDBFE`, background: "#EFF6FF",
          color: "#2563EB", fontWeight: 700, fontSize: 12.5, cursor: "pointer", margin: "10px 0",
        }}>Test workflow</button>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, margin: "10px 0" }}>
          <span style={{ fontSize: 12.5, color: wf.status === "published" ? ig.muted : ig.ink, fontWeight: 700 }}>Draft</span>
          <button data-testid="toggle-publish" onClick={() => save({ status: wf.status === "published" ? "draft" : "published" })}
            style={{
              width: 36, height: 20, borderRadius: 999, border: 0, padding: 0, cursor: "pointer",
              background: wf.status === "published" ? "#22C55E" : "#E5E7EB", position: "relative",
            }}>
            <span style={{ position: "absolute", top: 2, left: wf.status === "published" ? 18 : 2,
              width: 16, height: 16, borderRadius: 999, background: "#fff",
              boxShadow: "0 1px 4px rgba(0,0,0,0.2)", transition: "left 160ms" }} />
          </button>
          <span style={{ fontSize: 12.5, color: wf.status === "published" ? ig.ink : ig.muted, fontWeight: 700 }}>Publish</span>
        </div>
      </div>

      {/* CONTENT */}
      {tab === "builder" && (
        <Canvas wf={wf} triggers={triggers} actions={actions}
          onAddTrigger={() => setPickerOpen({ mode: "trigger" })}
          onAddAction={(afterId) => setPickerOpen({ mode: "action", afterNodeId: afterId })}
          onNodeOpen={(n) => setEditNode(n)}
          onNodeDuplicate={duplicateNode} onNodeDelete={removeNode}
        />
      )}
      {tab === "settings"  && <SettingsTab wf={wf} onSave={save} />}
      {tab === "history"   && <EnrollmentsTab items={enrollments} />}
      {tab === "logs"      && <LogsTab items={logs} />}

      {pickerOpen?.mode === "trigger" && (
        <TriggerPicker onClose={() => setPickerOpen(null)} onPick={(k, l) => addTrigger(k, l)} />
      )}
      {pickerOpen?.mode === "action" && (
        <ActionPicker onClose={() => setPickerOpen(null)} onPick={(k, l) => addAction(k, l, pickerOpen.afterNodeId)} />
      )}
      {editNode && (
        <NodeConfigDrawer node={editNode} onClose={() => setEditNode(null)}
          onSave={(updates) => { updateNode(editNode.id, updates); setEditNode(null); }}
          onDelete={() => { removeNode(editNode.id); setEditNode(null); }} />
      )}
    </div>
  );
}

/* ─────────────────────── CANVAS ─────────────────────── */

function Canvas({ wf, triggers, actions, onAddTrigger, onAddAction, onNodeOpen, onNodeDuplicate, onNodeDelete }) {
  return (
    <div style={{
      position: "relative", height: "calc(100vh - 230px)", overflow: "auto",
      backgroundImage: "radial-gradient(#D9E2DE 1px, transparent 1px)",
      backgroundSize: "22px 22px", backgroundColor: "#F4F7F5",
    }} className="ig-scroll">
      {/* Floating toolbar (left) */}
      <div style={{ position: "absolute", left: 14, top: 14, display: "flex", flexDirection: "column", gap: 8, zIndex: 5 }}>
        {["💬","⚠","◷","📄","↗","🔍","⏰","✨"].map((g, i) => (
          <button key={i} style={floatTool} data-testid={`canvas-tool-${i}`}>{g}</button>
        ))}
      </div>

      {/* Add (top right) */}
      <button data-testid="canvas-add" onClick={onAddTrigger} style={{
        position: "absolute", right: 22, top: 14, padding: "8px 14px", borderRadius: 10,
        border: `1px solid ${ig.border}`, background: ig.card, color: ig.ink,
        fontWeight: 700, fontSize: 12.5, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
      }}><Plus size={14} /> Add</button>

      {/* Zoom controls (bottom left) */}
      <div style={{ position: "absolute", left: 14, bottom: 100, display: "flex", flexDirection: "column", gap: 8, zIndex: 5 }}>
        <button style={floatTool}><Hand size={14} /></button>
      </div>
      <div style={{ position: "absolute", left: 14, bottom: 14, display: "flex", flexDirection: "column", gap: 6, zIndex: 5 }}>
        <button style={floatTool}><Plus size={14} /></button>
        <span style={{ ...floatTool, fontSize: 10.5, fontWeight: 800 }}>100%</span>
        <button style={floatTool}><Minus size={14} /></button>
        <button style={floatTool}><Maximize2 size={13} /></button>
      </div>

      {/* Mini-map (bottom right) */}
      <div style={{
        position: "absolute", right: 22, bottom: 22, width: 160, height: 100, borderRadius: 8,
        background: ig.card, border: `1px solid ${ig.border}`, boxShadow: ig.shadow, padding: 8,
      }}>
        <div style={{ display: "flex", gap: 4 }}>
          <div style={{ width: 30, height: 14, background: "#E2E8F0", borderRadius: 3 }} />
          <div style={{ width: 30, height: 14, background: "#E2E8F0", borderRadius: 3 }} />
          <div style={{ width: 30, height: 14, background: "#E2E8F0", borderRadius: 3 }} />
        </div>
        <div style={{ width: 30, height: 14, background: "#E2E8F0", borderRadius: 3, margin: "16px auto 6px" }} />
        <div style={{ width: 30, height: 14, background: "#E2E8F0", borderRadius: 3, margin: "0 auto" }} />
      </div>

      {/* Workflow content */}
      <div style={{ padding: "30px 80px 200px", display: "flex", flexDirection: "column", alignItems: "center", minHeight: 600 }}>
        {/* Triggers row */}
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center", marginBottom: 18 }}>
          {triggers.map((n) => (
            <NodeCard key={n.id} n={n} isTrigger
              onOpen={() => onNodeOpen(n)} onDup={() => onNodeDuplicate(n.id)} onDel={() => onNodeDelete(n.id)} />
          ))}
          <button data-testid="add-trigger" onClick={onAddTrigger} style={dashedNode}>
            <Plus size={14} color="#2563EB" />
            <span style={{ color: "#2563EB", fontWeight: 800, fontSize: 12.5 }}>Add new trigger</span>
          </button>
        </div>

        {/* Connector */}
        {(triggers.length > 0 || actions.length > 0) && <Connector />}
        <AddBetween onClick={() => onAddAction(triggers[triggers.length - 1]?.id)} />

        {/* Linear action chain */}
        {actions.map((n, i) => (
          <div key={n.id} style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Connector />
            <NodeCard n={n} onOpen={() => onNodeOpen(n)} onDup={() => onNodeDuplicate(n.id)} onDel={() => onNodeDelete(n.id)} />
            <Connector />
            <AddBetween onClick={() => onAddAction(n.id)} />
          </div>
        ))}
      </div>
    </div>
  );
}

function NodeCard({ n, isTrigger, onOpen, onDup, onDel }) {
  const style = NODE_STYLE[n.type] || { tone: "logic", glyph: "•" };
  const tone = TONES[style.tone] || TONES.logic;
  return (
    <div data-testid={`node-${n.id}`} onClick={onOpen} style={{
      width: 270, padding: 12, background: ig.card, borderRadius: 12,
      border: `1px solid ${n.error ? "#FECACA" : ig.border}`, boxShadow: ig.shadow,
      cursor: "pointer", position: "relative",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <span style={{ width: 30, height: 30, borderRadius: 8, display: "grid", placeItems: "center",
          background: tone.bg, color: tone.fg, fontSize: 14, fontWeight: 800 }}>{style.glyph}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 800, color: tone.fg }}>{n.label}</div>
        </div>
        <button onClick={(e) => { e.stopPropagation(); onDel(); }} style={miniBtn} title="Delete"><Trash2 size={11} color={ig.muted} /></button>
      </div>
      <div style={{ fontSize: 11.5, color: ig.muted, lineHeight: 1.5, marginBottom: 10,
        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
        {n.summary}
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center", borderTop: `1px solid ${ig.borderSoft}`, paddingTop: 8 }}>
        <button onClick={(e) => { e.stopPropagation(); onDup(); }} style={miniBtn} title="Duplicate"><Copy size={11} color={ig.muted} /></button>
        <div style={{ flex: 1 }} />
        {isTrigger && (
          <button data-testid={`node-stats-${n.id}`} style={{ ...miniBtn, color: "#2563EB", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <BarChart3 size={11} color="#2563EB" /> Stats
          </button>
        )}
      </div>
      {n.error && <span style={{ position: "absolute", right: -6, top: -6, width: 18, height: 18,
        borderRadius: 999, background: "#F59E0B", color: "#fff", display: "grid", placeItems: "center",
        boxShadow: "0 2px 6px rgba(245,158,11,0.4)" }}><AlertCircle size={11} /></span>}
    </div>
  );
}

function Connector() {
  return <div style={{ width: 1, height: 28, background: "#C5D2CB" }} />;
}

function AddBetween({ onClick }) {
  return (
    <button data-testid="add-between" onClick={onClick} style={{
      width: 26, height: 26, borderRadius: 999, border: `1.5px dashed #C5D2CB`,
      background: ig.card, color: "#94A3B0", cursor: "pointer",
      display: "grid", placeItems: "center", marginBottom: 0,
    }}>
      <Plus size={13} />
    </button>
  );
}

/* ─────────── Settings / History / Logs tabs ─────────── */

function SettingsTab({ wf, onSave }) {
  const s = wf.settings || {};
  return (
    <div style={{ padding: 28, maxWidth: 880, margin: "0 auto" }}>
      {[
        ["enrollment", "Enrollment", ["allow_multiple", "reentry_days", "stop_on_reply", "stop_on_appointment_booked"]],
        ["business_hours", "Business hours", ["use_account_hours", "skip_weekends", "timezone"]],
        ["communication", "Communication", ["respect_dnd", "respect_unsubscribe", "prevent_duplicates"]],
        ["ai_rules", "AI rules", ["draft_only", "human_approval", "confidence_threshold"]],
        ["error_handling", "Error handling", ["retry_failed", "retry_attempts", "notify_admin_on_failure", "stop_on_critical_error"]],
        ["notifications", "Notifications", ["notify_owner_on_reply", "notify_owner_on_appointment", "notify_admin_on_error"]],
      ].map(([key, title, fields]) => (
        <div key={key} data-testid={`settings-${key}`} style={{
          background: ig.card, border: `1px solid ${ig.border}`, borderRadius: 14, padding: 18, marginBottom: 14,
        }}>
          <div style={{ fontSize: 14, fontWeight: 800, marginBottom: 12 }}>{title}</div>
          {fields.map((f) => (
            <div key={f} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0",
              borderBottom: `1px dashed ${ig.borderSoft}`, fontSize: 12.5 }}>
              <span style={{ color: ig.muted, fontWeight: 600 }}>{f.replace(/_/g, " ")}</span>
              <span style={{ fontWeight: 700, color: ig.ink, fontFamily: "JetBrains Mono, monospace" }}>
                {String((s[key] || {})[f] ?? "—")}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function EnrollmentsTab({ items }) {
  if (!items) return <div style={{ padding: 40, color: ig.muted, textAlign: "center" }}>Loading…</div>;
  if (items.length === 0) return <div style={{ padding: 40, color: ig.muted, textAlign: "center" }}>No enrollments yet.</div>;
  return (
    <div style={{ padding: 22 }} data-testid="enrollments-tab">
      <div style={{ background: ig.card, border: `1px solid ${ig.border}`, borderRadius: 14, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr style={{ background: "#FAFCFB", color: ig.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.3 }}>
              <th style={th}>Contact</th><th style={th}>Email</th><th style={th}>Phone</th>
              <th style={th}>Status</th><th style={th}>Step</th><th style={th}>Steps done</th><th style={th}>Last action</th>
            </tr>
          </thead>
          <tbody>
            {items.map((e) => (
              <tr key={e.id} style={{ borderTop: `1px solid ${ig.borderSoft}` }}>
                <td style={td}><strong>{e.contact_name}</strong></td>
                <td style={{ ...td, color: ig.muted }}>{e.contact_email}</td>
                <td style={{ ...td, color: ig.muted, fontFamily: "JetBrains Mono, monospace" }}>{e.contact_phone}</td>
                <td style={td}><Badge tone={statusTone(e.status)}>{e.status}</Badge></td>
                <td style={{ ...td, fontFamily: "JetBrains Mono, monospace" }}>{e.current_node_id}</td>
                <td style={td}>{e.completed_steps}</td>
                <td style={{ ...td, color: ig.muted, fontSize: 11.5 }}>{niceDate(e.last_action_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LogsTab({ items }) {
  if (!items) return <div style={{ padding: 40, color: ig.muted, textAlign: "center" }}>Loading…</div>;
  if (items.length === 0) return <div style={{ padding: 40, color: ig.muted, textAlign: "center" }}>No execution logs yet.</div>;
  return (
    <div style={{ padding: 22 }} data-testid="logs-tab">
      <div style={{ background: ig.card, border: `1px solid ${ig.border}`, borderRadius: 14, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr style={{ background: "#FAFCFB", color: ig.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.3 }}>
              <th style={th}>When</th><th style={th}>Contact</th><th style={th}>Step</th>
              <th style={th}>Action</th><th style={th}>Status</th><th style={th}>Duration</th><th style={th}>Error</th>
            </tr>
          </thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.id} style={{ borderTop: `1px solid ${ig.borderSoft}` }}>
                <td style={{ ...td, color: ig.muted, fontSize: 11.5 }}>{niceDate(l.created_at)}</td>
                <td style={td}>{l.contact_name}</td>
                <td style={{ ...td, fontFamily: "JetBrains Mono, monospace" }}>{l.node_id}</td>
                <td style={td}>{l.action_type}</td>
                <td style={td}><Badge tone={logTone(l.status)}>{l.status}</Badge></td>
                <td style={{ ...td, fontFamily: "JetBrains Mono, monospace" }}>{l.duration_ms}ms</td>
                <td style={{ ...td, color: "#991B1B", fontSize: 11.5 }}>{l.error_reason || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function statusTone(s) {
  return ({ active: "info", completed: "success", paused: "warn", failed: "error", removed: "neutral", goal_met: "success" })[s] || "neutral";
}
function logTone(s) {
  return ({ success: "success", failed: "error", waiting: "warn", skipped: "neutral", retried: "warn", paused: "neutral" })[s] || "neutral";
}

function niceDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

const topGhost = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 10px",
  background: "transparent", border: `1px solid ${ig.border}`, borderRadius: 8,
  color: ig.ink, fontSize: 12, fontWeight: 700, cursor: "pointer",
};
const floatTool = {
  width: 36, height: 36, borderRadius: 9, border: `1px solid ${ig.border}`, background: ig.card,
  display: "grid", placeItems: "center", cursor: "pointer", color: ig.ink, fontSize: 14, padding: 0,
};
const dashedNode = {
  width: 230, padding: 22, borderRadius: 12, background: "rgba(37,99,235,0.05)",
  border: `2px dashed #BFDBFE`, color: "#2563EB", display: "inline-flex",
  alignItems: "center", justifyContent: "center", gap: 8, cursor: "pointer",
};
const miniBtn = {
  width: 22, height: 22, padding: 0, borderRadius: 5, border: 0, background: "transparent",
  display: "inline-grid", placeItems: "center", cursor: "pointer",
};
const th = { textAlign: "left", padding: "10px 14px", fontSize: 10.5, fontWeight: 800 };
const td = { padding: "11px 14px", color: ig.ink };
